import { createFileRoute } from '@tanstack/react-router';
import { Sun, Monitor, Moon } from 'lucide-react';
import { useMotionStore, useThemeStore, type Theme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';

export const Route = createFileRoute('/user/appearance')({
  component: AppearancePage,
});

// Same three modes as the header ThemeToggle, spelled out: this is the page you
// come to when the icon trio was too small to read.
const THEMES: { value: Theme; icon: typeof Sun; label: string; hint: string }[] = [
  { value: 'light', icon: Sun, label: 'Light', hint: 'Always the light palette.' },
  { value: 'system', icon: Monitor, label: 'System', hint: 'Follow your device setting.' },
  { value: 'dark', icon: Moon, label: 'Dark', hint: 'Always the dark palette.' },
];

function AppearancePage() {
  const { theme, setTheme } = useThemeStore();
  const { motion, setMotion } = useMotionStore();

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Appearance</h1>
        <p className="text-sm text-muted-foreground">
          How WordPuppi looks and moves on this device.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Theme</CardTitle>
          <CardDescription>Stored in this browser, not on your account.</CardDescription>
        </CardHeader>
        <CardContent>
          <div role="radiogroup" aria-label="Theme" className="grid gap-2 sm:grid-cols-3">
            {THEMES.map(({ value, icon: Icon, label, hint }) => {
              const active = theme === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setTheme(value)}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 text-left outline-none transition-colors motion-reduce:transition-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                    active
                      ? 'border-primary bg-accent'
                      : 'border-border hover:bg-accent/50',
                  )}
                >
                  <Icon className={cn('size-4 shrink-0 mt-0.5', active ? 'text-primary' : 'text-muted-foreground')} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{label}</span>
                    <span className="block text-xs text-muted-foreground">{hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Motion</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="ignore-reduce-motion">Ignore reduce motion</Label>
              <p className="text-xs text-muted-foreground">
                Play animations even when your system asks to reduce motion.
              </p>
            </div>
            <Switch
              id="ignore-reduce-motion"
              checked={motion === 'always'}
              onCheckedChange={(checked) => setMotion(checked ? 'always' : 'system')}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}
